/**
 * RoofNode - Generate roof geometry from a footprint shape
 *
 * Takes a three:shape (floor plan footprint) and generates roof geometry.
 * Supports flat, gable, hip, and pyramid styles.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';
import type { IEndpoint } from '@/runtime/IEndpoint';
import type { Payload } from '@/graph/Payload';
import { offsetPoints, removeSelfIntersections } from '../polygonUtils';
import { mergeGeometries } from '../geometryUtils';

const roofEndpoints = {
  ...internals({
    shapeIn: { dataType: 'three:shape', isInput: true, label: 'Footprint' },
    geometryOut: { dataType: 'three:geometry', isOutput: true },
  }),
  ...params({
    style: { dataType: 'string', value: 'gable', options: ['flat', 'gable', 'hip', 'pyramid', 'bevel'] },
    height: { value: 2, min: 0.1, max: 10, step: 0.1, label: 'Ridge Height' },
    overhang: { value: 0.2, min: 0, max: 2, step: 0.05 },
    baseHeight: { value: 0, min: 0, max: 20, step: 0.1, label: 'Base Elevation' },
  }),
};

class RoofNode extends BaseNode<typeof roofEndpoints> {
  private three: Three;
  private shape: any = null;
  private mesh: any = null;
  private rebuildCallback: (() => void) | null = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  buildGeometry(): any {
    if (!this.shape) return null;

    const style = this.params.style.value as string;
    const ridgeHeight = this.params.height.value;
    const overhang = this.params.overhang.value;
    const baseY = this.params.baseHeight.value;

    // Extract bounding box from shape points
    const pts = this.shape.getPoints?.(64) ?? [];
    if (pts.length < 3) return null;

    let minX = Infinity, maxX = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    for (const p of pts) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minZ) minZ = p.y;
      if (p.y > maxZ) maxZ = p.y;
    }

    // Apply overhang — expand base footprint outward
    minX -= overhang;
    maxX += overhang;
    minZ -= overhang;
    maxZ += overhang;

    const w = maxX - minX;
    const d = maxZ - minZ;
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;

    let geometry: any;

    switch (style) {
      case 'flat':
        geometry = this.buildFlat(minX, maxX, minZ, maxZ, baseY);
        break;
      case 'gable':
        geometry = this.buildGable(minX, maxX, minZ, maxZ, cx, cz, w, d, ridgeHeight, baseY);
        break;
      case 'hip':
        geometry = this.buildHip(minX, maxX, minZ, maxZ, cx, cz, w, d, ridgeHeight, baseY);
        break;
      case 'pyramid':
        geometry = this.buildPyramid(minX, maxX, minZ, maxZ, cx, cz, ridgeHeight, baseY);
        break;
      case 'bevel':
        geometry = this.buildBevel(pts, ridgeHeight, overhang, baseY);
        break;
      default:
        geometry = this.buildFlat(minX, maxX, minZ, maxZ, baseY);
    }

    geometry.computeVertexNormals();
    return geometry;
  }

  /** Flat roof — single horizontal quad at baseHeight */
  private buildFlat(
    minX: number, maxX: number, minZ: number, maxZ: number, baseY: number
  ): any {
    const positions = new Float32Array([
      // Triangle 1
      minX, baseY, minZ,
      maxX, baseY, maxZ,
      maxX, baseY, minZ,
      // Triangle 2
      minX, baseY, minZ,
      minX, baseY, maxZ,
      maxX, baseY, maxZ,
    ]);

    const geometry = new this.three.BufferGeometry();
    geometry.setAttribute('position', new this.three.BufferAttribute(positions, 3));
    return geometry;
  }

  /** Gable roof — two sloped planes meeting at ridge along longer axis */
  private buildGable(
    minX: number, maxX: number, minZ: number, maxZ: number,
    cx: number, cz: number, w: number, d: number,
    ridgeHeight: number, baseY: number
  ): any {
    const ridgeY = baseY + ridgeHeight;

    // Ridge runs along the longer axis
    const alongX = w >= d;

    let positions: Float32Array;

    if (alongX) {
      // Ridge from (minX, ridgeY, cz) to (maxX, ridgeY, cz)
      // Left slope: minZ base to ridge
      // Right slope: ridge to maxZ base
      positions = new Float32Array([
        // Left slope — 2 triangles
        minX, baseY, minZ,   maxX, ridgeY, cz,    maxX, baseY, minZ,
        minX, baseY, minZ,   minX, ridgeY, cz,    maxX, ridgeY, cz,
        // Right slope — 2 triangles
        minX, ridgeY, cz,    maxX, baseY, maxZ,   maxX, ridgeY, cz,
        minX, ridgeY, cz,    minX, baseY, maxZ,   maxX, baseY, maxZ,
        // Left gable triangle
        minX, baseY, minZ,   minX, baseY, maxZ,   minX, ridgeY, cz,
        // Right gable triangle
        maxX, baseY, minZ,   maxX, ridgeY, cz,    maxX, baseY, maxZ,
      ]);
    } else {
      // Ridge from (cx, ridgeY, minZ) to (cx, ridgeY, maxZ)
      // Front slope: minX base to ridge
      // Back slope: ridge to maxX base
      positions = new Float32Array([
        // Front slope — 2 triangles
        minX, baseY, minZ,   cx, ridgeY, maxZ,    cx, ridgeY, minZ,
        minX, baseY, minZ,   minX, baseY, maxZ,   cx, ridgeY, maxZ,
        // Back slope — 2 triangles
        cx, ridgeY, minZ,    maxX, baseY, maxZ,   maxX, baseY, minZ,
        cx, ridgeY, minZ,    cx, ridgeY, maxZ,    maxX, baseY, maxZ,
        // Front gable triangle
        minX, baseY, minZ,   cx, ridgeY, minZ,    maxX, baseY, minZ,
        // Back gable triangle
        minX, baseY, maxZ,   maxX, baseY, maxZ,   cx, ridgeY, maxZ,
      ]);
    }

    const geometry = new this.three.BufferGeometry();
    geometry.setAttribute('position', new this.three.BufferAttribute(positions, 3));
    return geometry;
  }

  /** Hip roof — four sloped planes meeting at a shorter ridge */
  private buildHip(
    minX: number, maxX: number, minZ: number, maxZ: number,
    cx: number, cz: number, w: number, d: number,
    ridgeHeight: number, baseY: number
  ): any {
    const ridgeY = baseY + ridgeHeight;
    const alongX = w >= d;

    // Ridge is 50% the length of the longer axis, centered
    const ridgeLen = (alongX ? w : d) * 0.25;

    let r0x: number, r0z: number, r1x: number, r1z: number;

    if (alongX) {
      r0x = cx - ridgeLen; r0z = cz;
      r1x = cx + ridgeLen; r1z = cz;
    } else {
      r0x = cx; r0z = cz - ridgeLen;
      r1x = cx; r1z = cz + ridgeLen;
    }

    // 4 corners of base
    const bl = [minX, baseY, minZ]; // bottom-left
    const br = [maxX, baseY, minZ]; // bottom-right
    const tr = [maxX, baseY, maxZ]; // top-right
    const tl = [minX, baseY, maxZ]; // top-left

    const ridge0 = [r0x, ridgeY, r0z];
    const ridge1 = [r1x, ridgeY, r1z];

    // Build 6 triangles for the 4 faces
    const tris: number[] = [];

    const tri = (a: number[], b: number[], c: number[]) => {
      tris.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
    };

    if (alongX) {
      // Front face (minZ side) — 2 triangles
      tri(bl, ridge1, br);
      tri(bl, ridge0, ridge1);
      // Back face (maxZ side) — 2 triangles
      tri(tr, ridge0, tl);
      tri(tr, ridge1, ridge0);
      // Left hip triangle (minX side)
      tri(bl, tl, ridge0);
      // Right hip triangle (maxX side)
      tri(br, ridge1, tr);
    } else {
      // Front face (minX side) — 2 triangles
      tri(bl, tl, ridge0);
      tri(tl, ridge1, ridge0);
      // Back face (maxX side) — 2 triangles
      tri(br, ridge1, tr);
      tri(br, ridge0, ridge1);
      // Bottom hip triangle (minZ side)
      tri(bl, ridge0, br);
      // Top hip triangle (maxZ side)
      tri(tl, tr, ridge1);
    }

    const positions = new Float32Array(tris);
    const geometry = new this.three.BufferGeometry();
    geometry.setAttribute('position', new this.three.BufferAttribute(positions, 3));
    return geometry;
  }

  /** Pyramid roof — four triangles meeting at center apex */
  private buildPyramid(
    minX: number, maxX: number, minZ: number, maxZ: number,
    cx: number, cz: number,
    ridgeHeight: number, baseY: number
  ): any {
    const apex = [cx, baseY + ridgeHeight, cz];
    const bl = [minX, baseY, minZ];
    const br = [maxX, baseY, minZ];
    const tr = [maxX, baseY, maxZ];
    const tl = [minX, baseY, maxZ];

    const tris: number[] = [];
    const tri = (a: number[], b: number[], c: number[]) => {
      tris.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
    };

    tri(bl, apex, br); // front (minZ)
    tri(br, apex, tr); // right (maxX)
    tri(tr, apex, tl); // back (maxZ)
    tri(tl, apex, bl); // left (minX)

    const positions = new Float32Array(tris);
    const geometry = new this.three.BufferGeometry();
    geometry.setAttribute('position', new this.three.BufferAttribute(positions, 3));
    return geometry;
  }

  /** Bevel roof — insets the actual polygon footprint and builds sloped faces */
  private buildBevel(
    rawPts: Array<{ x: number; y: number }>,
    ridgeHeight: number, overhang: number, baseY: number
  ): any {
    // Convert shape points to simple {x, y}, remove duplicate closing point
    let points = rawPts.map((p: any) => ({ x: p.x, y: p.y }));
    if (points.length > 3) {
      const first = points[0];
      const last = points[points.length - 1];
      const dist = Math.sqrt((first.x - last.x) ** 2 + (first.y - last.y) ** 2);
      if (dist < 0.0001) {
        points = points.slice(0, -1);
      }
    }
    if (points.length < 3) return new this.three.BufferGeometry();

    // Outer ring: expand outward for eaves overhang
    const outerRing = overhang > 0
      ? removeSelfIntersections(offsetPoints(points, overhang))
      : points;

    // Inner ring: inset by ridgeHeight (steeper = deeper inset)
    const rawInner = offsetPoints(points, -ridgeHeight);
    const innerRing = removeSelfIntersections(rawInner);

    const ridgeY = baseY + ridgeHeight;

    // Check if inner polygon degenerated — fall back to pyramid (all to centroid)
    if (innerRing.length < 3) {
      // Compute centroid of outer ring
      let cx = 0, cy = 0;
      for (const p of outerRing) { cx += p.x; cy += p.y; }
      cx /= outerRing.length;
      cy /= outerRing.length;

      const apex = [cx, ridgeY, cy];
      const tris: number[] = [];
      for (let i = 0; i < outerRing.length; i++) {
        const cur = outerRing[i];
        const next = outerRing[(i + 1) % outerRing.length];
        tris.push(
          cur.x, baseY, cur.y,
          apex[0], apex[1], apex[2],
          next.x, baseY, next.y
        );
      }
      const geo = new this.three.BufferGeometry();
      geo.setAttribute('position', new this.three.BufferAttribute(new Float32Array(tris), 3));
      return geo;
    }

    // Build sloped faces between outer ring (baseY) and inner ring (ridgeY)
    const slopeTris: number[] = [];
    const n = outerRing.length;
    const m = innerRing.length;

    if (n === m) {
      // 1:1 correspondence — simple triangle strip
      for (let i = 0; i < n; i++) {
        const o0 = outerRing[i];
        const o1 = outerRing[(i + 1) % n];
        const i0 = innerRing[i];
        const i1 = innerRing[(i + 1) % m];

        // Triangle 1: outer[i], outer[i+1], inner[i]
        slopeTris.push(
          o0.x, baseY, o0.y,
          o1.x, baseY, o1.y,
          i0.x, ridgeY, i0.y
        );
        // Triangle 2: inner[i], outer[i+1], inner[i+1]
        slopeTris.push(
          i0.x, ridgeY, i0.y,
          o1.x, baseY, o1.y,
          i1.x, ridgeY, i1.y
        );
      }
    } else {
      // Different vertex counts — fan from each outer edge to centroid of inner
      let cx = 0, cy = 0;
      for (const p of innerRing) { cx += p.x; cy += p.y; }
      cx /= innerRing.length;
      cy /= innerRing.length;

      for (let i = 0; i < n; i++) {
        const o0 = outerRing[i];
        const o1 = outerRing[(i + 1) % n];
        slopeTris.push(
          o0.x, baseY, o0.y,
          o1.x, baseY, o1.y,
          cx, ridgeY, cy
        );
      }
    }

    const slopeGeo = new this.three.BufferGeometry();
    slopeGeo.setAttribute('position', new this.three.BufferAttribute(new Float32Array(slopeTris), 3));

    // Build flat cap from inner ring using ShapeGeometry (earcut triangulation)
    const capShape = new this.three.Shape();
    capShape.moveTo(innerRing[0].x, innerRing[0].y);
    for (let i = 1; i < innerRing.length; i++) {
      capShape.lineTo(innerRing[i].x, innerRing[i].y);
    }
    capShape.closePath();

    const capGeo2D = new this.three.ShapeGeometry(capShape);

    // Remap ShapeGeometry positions: (x, y, 0) → (x, ridgeY, y)
    const capPos = capGeo2D.getAttribute('position');
    const capCount = capPos.count;
    const remapped = new Float32Array(capCount * 3);
    for (let i = 0; i < capCount; i++) {
      remapped[i * 3] = capPos.getX(i);
      remapped[i * 3 + 1] = ridgeY;
      remapped[i * 3 + 2] = capPos.getY(i);
    }

    const capGeo = new this.three.BufferGeometry();
    capGeo.setAttribute('position', new this.three.BufferAttribute(remapped, 3));
    if (capGeo2D.index) {
      capGeo.setIndex(capGeo2D.index);
    }
    capGeo2D.dispose();

    // Merge slope faces + cap
    const merged = mergeGeometries([slopeGeo, capGeo]);
    slopeGeo.dispose();
    capGeo.dispose();

    return merged ?? new this.three.BufferGeometry();
  }

  setThreeObject(mesh: any): void {
    this.mesh = mesh;
  }

  setRebuildCallback(callback: () => void): void {
    this.rebuildCallback = callback;
  }

  animateCallback = () => {
    if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  };

  onInternalChanged(payload: Payload, endpointId: string, _source: IEndpoint): void {
    if (endpointId === 'shapeIn') {
      this.shape = payload?.value;
      if (this.mesh) {
        const oldGeo = this.mesh.geometry;
        this.mesh.geometry = this.buildGeometry();
        if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
      }
    }
  }

  onDisconnectedFrom(endpointId: string, _source: IEndpoint): void {
    if (endpointId === 'shapeIn') {
      this.shape = null;
    }
  }

  onParamChanged(): void {
    if (this.rebuildCallback) {
      this.rebuildCallback();
    } else if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  }
}

export const roofNodeDef: NodeDef<RoofNode> = {
  type: 'Roof',
  shortName: 'roof',
  info: 'Generates roof geometry from a footprint shape. Supports flat, gable, hip, and pyramid styles.',
  category: 'architecture',
  endpoints: roofEndpoints,
  ctor: RoofNode,
};
